import { Dialog, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box, Button, IconButton, Tooltip, Chip, useMediaQuery, useTheme } from '@mui/material';
import { collection, onSnapshot, orderBy, query, where, doc, getDoc, updateDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import React, { useEffect, useState, useMemo } from 'react';
import { DATABASE } from '../../../Config/firebase';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Refresh as RefreshIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
    buildBalanceSummary,
    buildStatementRows,
    classifyPendingTransactions,
    formatProfitPercentDisplay,
    getTransactionTypeColor,
    getTransactionTypeName,
    LEDGER_TYPE,
} from '../../../Utils/ledgerStatementUtils';
const ViewLedgersModel = (props) => {
    const {
        open,
        onClose,
        selectCustomerLedgersID,
        customerList = [],
        onSelectCustomer,
    } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [ledgers, setLedgers] = React.useState([]);
    const [pendingTransactions, setPendingTransactions] = useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [userNetBalance, setUserNetBalance] = useState(0);
    const [customerName, setCustomerName] = useState("");

    // Navegação entre clientes (índice atual + total)
    const currentIndex = useMemo(() => {
        if (!Array.isArray(customerList) || !selectCustomerLedgersID) return -1;
        return customerList.findIndex((c) => c?.id === selectCustomerLedgersID);
    }, [customerList, selectCustomerLedgersID]);

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < (customerList?.length || 0) - 1;

    const goToPrev = React.useCallback(() => {
        if (!hasPrev || typeof onSelectCustomer !== 'function') return;
        const prev = customerList[currentIndex - 1];
        if (prev?.id) onSelectCustomer(prev.id);
    }, [hasPrev, customerList, currentIndex, onSelectCustomer]);

    const goToNext = React.useCallback(() => {
        if (!hasNext || typeof onSelectCustomer !== 'function') return;
        const next = customerList[currentIndex + 1];
        if (next?.id) onSelectCustomer(next.id);
    }, [hasNext, customerList, currentIndex, onSelectCustomer]);
    const getLedgersList = () => {
        setIsLoading(true);
        const q = query(
            collection(DATABASE, "ledgers"),
            orderBy('date', "desc"),
            where("user_id", "==", selectCustomerLedgersID)
        );
        return onSnapshot(q, async (querySnapshot) => {
            const allData = await Promise.all(
                querySnapshot?.docs
                    .filter(ledgerDocSnapshot => ledgerDocSnapshot?.data())
                    .map(async (ledgerDocSnapshot) => {
                        const ledgerData = ledgerDocSnapshot.data();
                        const ledgerDoc = {
                            id: ledgerDocSnapshot.id,
                            ...ledgerData,
                        };
                        
                        // BUSCAR TRANSAÇÃO: Buscar status e datas da transação correspondente
                        // Nem toda transação cria ledger, mas quando cria, deve ter relação com a transação
                        let txnData = null;
                        
                        // Tentativa 1: Buscar por doc_id do campo dentro do ledger (preferencial)
                        if (ledgerData.doc_id) {
                            try {
                                const txnRef = doc(DATABASE, "transection_requests", ledgerData.doc_id);
                                const txnDoc = await getDoc(txnRef);
                                if (txnDoc.exists()) {
                                    txnData = txnDoc.data();
                                    console.log(`✅ Found transaction using ledgerData.doc_id: ${ledgerData.doc_id}`);
                                }
                            } catch (error) {
                                console.log(`⚠️ Transaction not found using ledgerData.doc_id: ${ledgerData.doc_id}`, error);
                                // doc_id não é válido ou não corresponde, continuar para busca alternativa
                            }
                        }
                        
                        // Tentativa 2: Se doc_id não funcionou, tentar usar o ID do documento do ledger como ID da transação
                        // (Alguns ledgers podem ter sido criados com o ID do documento igual ao ID da transação)
                        if (!txnData && ledgerDocSnapshot.id) {
                            try {
                                const txnRef = doc(DATABASE, "transection_requests", ledgerDocSnapshot.id);
                                const txnDoc = await getDoc(txnRef);
                                if (txnDoc.exists()) {
                                    const txn = txnDoc.data();
                                    // Verificar se a transação corresponde ao ledger (mesmo user_id e amount)
                                    if (txn.user_id === ledgerData.user_id && 
                                        (txn.total_amount === ledgerData.new_txn_amount || 
                                         Math.abs((txn.total_amount || 0) - (ledgerData.new_txn_amount || 0)) < 0.01)) {
                                        txnData = txn;
                                        console.log(`✅ Found transaction using ledger document ID: ${ledgerDocSnapshot.id}`);
                                    }
                                }
                            } catch (error) {
                                console.log(`⚠️ Transaction not found using ledger document ID: ${ledgerDocSnapshot.id}`, error);
                            }
                        }
                        
                        // Tentativa 3: Se ainda não encontrou, buscar por user_id + date + amount (busca mais ampla)
                        // ledger.date = conclusão = transaction.request_approval_time (quando foi aprovado/pago)
                        // ledger.new_txn_amount = transaction.total_amount
                        if (!txnData && ledgerData.user_id && ledgerData.date && ledgerData.new_txn_amount !== undefined) {
                            try {
                                const transactionsQuery = query(
                                    collection(DATABASE, "transection_requests"),
                                    where("user_id", "==", ledgerData.user_id),
                                    where("total_amount", "==", ledgerData.new_txn_amount)
                                );
                                const transactionsSnapshot = await getDocs(transactionsQuery);
                                
                                // Converter ledger.date para Date object para comparação
                                const ledgerDate = ledgerData.date?.toDate ? ledgerData.date.toDate() : new Date(ledgerData.date);
                                
                                // Encontrar a transação que corresponde ao ledger.date
                                // Tentar primeiro request_approval_time, depois date (para transações canceladas)
                                let bestMatch = null;
                                let bestTimeDiff = Infinity;
                                
                                for (const txnDoc of transactionsSnapshot.docs) {
                                    const txn = txnDoc.data();
                                    let txnDate = null;
                                    
                                    // Prioridade 1: request_approval_time (quando foi aprovado/pago)
                                    if (txn.request_approval_time) {
                                        txnDate = txn.request_approval_time?.toDate ? txn.request_approval_time.toDate() : new Date(txn.request_approval_time);
                                    } 
                                    // Prioridade 2: date (para transações canceladas ou que não foram aprovadas)
                                    else if (txn.date) {
                                        txnDate = txn.date?.toDate ? txn.date.toDate() : new Date(txn.date);
                                    }
                                    
                                    if (txnDate) {
                                        const timeDiff = Math.abs(ledgerDate.getTime() - txnDate.getTime());
                                        // Encontrar a transação mais próxima (pode haver múltiplas com mesmo amount)
                                        if (timeDiff < bestTimeDiff && timeDiff < 120000) { // 2 minutos de tolerância
                                            bestMatch = txn;
                                            bestTimeDiff = timeDiff;
                                        }
                                    }
                                }
                                
                                if (bestMatch) {
                                    txnData = bestMatch;
                                    console.log(`✅ Found transaction using user_id + amount + date match (time diff: ${bestTimeDiff}ms)`);
                                }
                            } catch (error) {
                                console.error(`Error searching transaction for ledger:`, error);
                            }
                        }
                        
                        // Se encontrou transação, usar os dados dela
                        if (txnData) {
                            ledgerDoc.request_status = txnData.request_status;
                            ledgerDoc.request_date = txnData.date; // Original request date
                            // Conclusion date: usar request_approval_time se existir, senão usar date (para canceladas)
                            ledgerDoc.conclusion_date = txnData.request_approval_time || txnData.date || ledgerData.date;
                            ledgerDoc.yield_product_name = txnData.yield_product_name || null;
                            // IMPORTANTE: Atualizar doc_id no ledgerDoc se encontrou a transação
                            // Isso garante que o doc_id está correto para futuras buscas
                            if (!ledgerDoc.doc_id || ledgerDoc.doc_id !== txnData.doc_id) {
                                ledgerDoc.doc_id = txnData.doc_id || ledgerData.doc_id;
                            }
                        } else {
                            // Se não encontrou transação, usar dados do ledger
                            // NOTA: Ledgers de performance (3) e yield final (4) podem não ter txn correspondente — normal
                            // Ledgers antigos também podem não ter transações (criados antes da correção do doc_id)
                            // Só mostrar warning se NÃO for performance nem yield
                            if (ledgerData.transection_type !== 3 && ledgerData.transection_type !== 4) {
                                // Apenas logar como info (não warning) para ledgers que deveriam ter transação mas não têm
                                console.info(`ℹ️ Transaction not found for ledger ${ledgerDocSnapshot.id} (type: ${ledgerData.transection_type}). doc_id: ${ledgerData.doc_id}, amount: ${ledgerData.new_txn_amount}. This may be normal for old ledgers.`);
                            }
                            if (!ledgerDoc.request_status) {
                                ledgerDoc.request_status = ledgerData.request_status; // Pode ser undefined, mas ok
                            }
                            ledgerDoc.request_date = ledgerData.request_date || ledgerData.date;
                            ledgerDoc.conclusion_date = ledgerData.conclusion_date || ledgerData.date;
                        }
                        
                        return ledgerDoc;
                    })
            );
            setLedgers(allData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching ledgers:", error);
            setIsLoading(false);
        });
    };

    useEffect(() => {
        if (selectCustomerLedgersID) {
            getLedgersList();
        }
    }, [selectCustomerLedgersID]);

    // Transações pendentes para conciliação (read-only — não altera extrato oficial)
    useEffect(() => {
        if (!selectCustomerLedgersID) {
            setPendingTransactions([]);
            return undefined;
        }

        const pendingQuery = query(
            collection(DATABASE, 'transection_requests'),
            where('user_id', '==', selectCustomerLedgersID)
        );

        const unsubscribe = onSnapshot(
            pendingQuery,
            (snapshot) => {
                const txns = snapshot.docs.map((txnDoc) => ({
                    id: txnDoc.id,
                    ...txnDoc.data(),
                }));
                setPendingTransactions(txns);
            },
            (error) => {
                console.error('Error fetching pending transactions:', error);
                setPendingTransactions([]);
            }
        );

        return () => unsubscribe();
    }, [selectCustomerLedgersID]);

    // Use real-time listener for user balance to ensure it's always up-to-date
    useEffect(() => {
        if (!selectCustomerLedgersID) return;

        // Pré-popular nome a partir da lista enquanto o snapshot não chega
        const fromList = customerList?.find?.((c) => c?.id === selectCustomerLedgersID);
        if (fromList?.name) setCustomerName(fromList.name);

        const userRef = doc(DATABASE, "users", selectCustomerLedgersID);

        // Set up real-time listener for user balance
        const unsubscribe = onSnapshot(userRef, (userDoc) => {
            if (userDoc.exists()) {
                const userData = userDoc.data();
                const latestBalance = parseFloat(userData?.net_balance || 0);
                console.log(`[ViewLedgersModel] User balance updated: $${latestBalance.toFixed(2)}`);
                setUserNetBalance(latestBalance);
                setCustomerName(userData?.full_name || userData?.email || fromList?.name || "");
            } else {
                setUserNetBalance(0);
            }
        }, (error) => {
            console.error("Error listening to user balance:", error);
            // Fallback to one-time fetch if listener fails
            getDoc(userRef).then((userDoc) => {
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUserNetBalance(parseFloat(userData?.net_balance || 0));
                    setCustomerName(userData?.full_name || userData?.email || fromList?.name || "");
                }
            });
        });

        return () => unsubscribe();
    }, [selectCustomerLedgersID, customerList]);

    // Atalhos de teclado: ←/→ para navegar entre clientes
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (event) => {
            // Ignora quando o usuário está digitando em input/textarea/contenteditable
            const target = event.target;
            const tag = (target?.tagName || '').toLowerCase();
            const isEditable = target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
            if (isEditable) return;

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goToPrev();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                goToNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, goToPrev, goToNext]);

    // Force refresh user balance and verify against ledgers
    const forceRefreshBalance = async () => {
        if (!selectCustomerLedgersID) return;
        
        try {
            const userRef = doc(DATABASE, "users", selectCustomerLedgersID);
            
            // Get current balance from Firestore
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "User not found"
                });
                return;
            }
            
            const userData = userDoc.data();
            const currentBalance = parseFloat(userData?.net_balance || 0);
            
            // Calculate expected balance from ledgers (most recent by effective accounting date)
            let calculatedBalance = currentBalance;
            const statementRows = buildStatementRows(ledgers);
            if (statementRows.length > 0 && statementRows[0].displayBalance !== null) {
                calculatedBalance = parseFloat(statementRows[0].displayBalance);
            }
            
            // Update timestamp to trigger Firestore listeners (like Flutter stream)
            await updateDoc(userRef, {
                _last_balance_sync: serverTimestamp()
            });
            
            // Update local state
            setUserNetBalance(currentBalance);
            
            // Show diagnostic information
            const balanceMatch = Math.abs(currentBalance - calculatedBalance) < 0.01;
            
            Swal.fire({
                icon: balanceMatch ? "success" : "warning",
                title: "Balance Diagnostic",
                html: `
                    <div style="text-align: left;">
                        <p><strong>Current Balance (Firestore):</strong> $${currentBalance.toFixed(2)}</p>
                        <p><strong>Calculated from Ledgers:</strong> $${calculatedBalance.toFixed(2)}</p>
                        <p><strong>Difference:</strong> $${Math.abs(currentBalance - calculatedBalance).toFixed(2)}</p>
                        ${!balanceMatch ? '<p style="color: red;"><strong>⚠️ Warning:</strong> Balance mismatch detected!</p>' : ''}
                        <p style="margin-top: 10px;">The Flutter app should update automatically.</p>
                    </div>
                `,
                width: 500
            });
        } catch (error) {
            console.error("Error refreshing balance:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Failed to refresh balance: ${error.message}`
            });
        }
    };

    // Use actual user net_balance from users collection instead of ledger balance
    // This ensures consistency with the app and CustomerManagement table
    const latestNetBalance = userNetBalance;
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };
    
    // Format transaction status for display
    const formatStatus = (status) => {
        if (status === undefined || status === null) return 'N/A';
        const statusMap = {
            0: 'Pending',
            1: 'Processing',
            2: 'Approved',
            3: 'Cancelled',
            4: 'Rejected',
            5: 'Paid'
        };
        return statusMap[status] || `Status ${status}`;
    };
    
    // Get status color
    const getStatusColor = (status) => {
        if (status === undefined || status === null) return 'inherit';
        const colorMap = {
            0: '#ff9800', // Orange - Pending
            1: '#2196f3', // Blue - Processing
            2: '#4caf50', // Green - Approved
            3: '#f44336', // Red - Cancelled
            4: '#f44336', // Red - Rejected
            5: '#4caf50'  // Green - Paid
        };
        return colorMap[status] || 'inherit';
    };
    
    // Get transaction type name — imported from ledgerStatementUtils
    
    // Format date in compact format: 25/11/25 13:12:20 PM
    const formatCompactDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            let date;
            if (timestamp?.toDate) {
                date = timestamp.toDate();
            } else if (timestamp?.seconds) {
                date = new Date(timestamp.seconds * 1000);
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else {
                return 'N/A';
            }
            
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            
            return `${day}/${month}/${year} ${displayHours}:${minutes}:${seconds} ${ampm}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    const ledgersWithBalance = useMemo(() => buildStatementRows(ledgers), [ledgers]);

    const pendingSummary = useMemo(
        () => classifyPendingTransactions(pendingTransactions),
        [pendingTransactions]
    );

    const balanceSummary = useMemo(
        () =>
            buildBalanceSummary({
                clientAppBalance: userNetBalance,
                statementRows: ledgersWithBalance,
                pendingDeductedTotal: pendingSummary.pendingDeductedTotal,
            }),
        [userNetBalance, ledgersWithBalance, pendingSummary.pendingDeductedTotal]
    );

    const handleFixBalance = async () => {
        if (!selectCustomerLedgersID || ledgersWithBalance.length === 0) return;

        try {
            const mostRecentLedger = ledgersWithBalance[0];
            const ledgerBalance = parseFloat(
                mostRecentLedger.displayBalance ??
                    mostRecentLedger.current_balance ??
                    mostRecentLedger.balance ??
                    0
            );
            const currentBalance = userNetBalance;

            if (balanceSummary.shouldBlockFixBalance) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Fix Balance Blocked',
                    html: `
                        <div style="text-align: left;">
                            <p>The difference between <strong>Client App Balance</strong> and <strong>Statement Balance</strong> is explained by pending transactions.</p>
                            <p><strong>Client App Balance:</strong> $${balanceSummary.clientAppBalance.toFixed(2)}</p>
                            <p><strong>Statement Balance:</strong> $${balanceSummary.statementBalance.toFixed(2)}</p>
                            <p><strong>Pending (deducted from app):</strong> -$${balanceSummary.pendingDeductedTotal.toFixed(2)}</p>
                            <p style="margin-top: 10px;">Fix Balance would incorrectly overwrite the client's app balance. Resolve or pay pending transactions first.</p>
                        </div>
                    `,
                    confirmButtonText: 'OK',
                });
                return;
            }

            if (Math.abs(ledgerBalance - currentBalance) <= 0.01) {
                Swal.fire({
                    icon: 'info',
                    title: 'Balance is correct',
                    text: 'The balance already matches the ledger.',
                    timer: 2000,
                });
                return;
            }

            const result = await Swal.fire({
                title: 'Fix Balance?',
                html: `
                    <div style="text-align: left;">
                        <p><strong>Client App Balance (Firestore):</strong> $${currentBalance.toFixed(2)}</p>
                        <p><strong>Statement Balance (latest ledger):</strong> $${ledgerBalance.toFixed(2)}</p>
                        <p><strong>Difference:</strong> $${Math.abs(ledgerBalance - currentBalance).toFixed(2)}</p>
                        <p style="margin-top: 10px;">This will update Firestore net_balance to match the most recent ledger entry.</p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, fix it',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) return;

            const userRef = doc(DATABASE, 'users', selectCustomerLedgersID);
            await updateDoc(userRef, {
                net_balance: ledgerBalance,
                _last_balance_sync: serverTimestamp(),
            });

            Swal.fire({
                icon: 'success',
                title: 'Balance Fixed!',
                text: `Balance updated to $${ledgerBalance.toFixed(2)}`,
                timer: 2000,
            });
        } catch (error) {
            console.error('Error in Fix Balance:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `An error occurred: ${error.message}`,
            });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "lg"}
            fullWidth
            fullScreen={isMobile}
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: isMobile ? 0 : "20px",
                    height: isMobile ? "100%" : "90vh",
                    maxHeight: isMobile ? "none" : "900px",
                    overflow: "scroll",
                    "::-webkit-scrollbar": { width: "0px", height: '0px' },
                },
                "& .MuiDialogContent-root": {
                    overflowY: "initial",
                },
                "& .MuiBackdrop-root": {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    {/* Seta anterior */}
                    <Tooltip title={hasPrev ? `Previous: ${customerList?.[currentIndex - 1]?.name || ''} (←)` : 'No previous customer'}>
                        <span>
                            <IconButton
                                onClick={goToPrev}
                                disabled={!hasPrev}
                                size="small"
                                sx={{
                                    background: hasPrev ? 'rgba(58, 222, 204, 0.15)' : 'transparent',
                                    '&:hover': { background: 'rgba(58, 222, 204, 0.25)' },
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Seta próxima */}
                    <Tooltip title={hasNext ? `Next: ${customerList?.[currentIndex + 1]?.name || ''} (→)` : 'No next customer'}>
                        <span>
                            <IconButton
                                onClick={goToNext}
                                disabled={!hasNext}
                                size="small"
                                sx={{
                                    background: hasNext ? 'rgba(58, 222, 204, 0.15)' : 'transparent',
                                    '&:hover': { background: 'rgba(58, 222, 204, 0.25)' },
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Título com nome do cliente */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, ml: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Customer Ledgers
                                {customerName ? (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            color: '#3adecc',
                                            ml: 1,
                                        }}
                                    >
                                        — {customerName}
                                    </Typography>
                                ) : null}
                            </Typography>
                            <Tooltip title="Force refresh balance to sync with Flutter app">
                                <IconButton onClick={forceRefreshBalance} size="small" color="primary">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {currentIndex >= 0 && customerList?.length > 0 && (
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>
                                {currentIndex + 1} of {customerList.length}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {ledgersWithBalance && ledgersWithBalance.length > 0 && (
                    <Tooltip
                        title={
                            balanceSummary.shouldBlockFixBalance
                                ? 'Blocked: difference explained by pending transactions'
                                : 'Fix balance using most recent ledger'
                        }
                    >
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                disabled={balanceSummary.shouldBlockFixBalance}
                                onClick={handleFixBalance}
                            >
                                Fix Balance
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><Skeleton width={80} /></TableCell>
                                    <TableCell><Skeleton width={80} /></TableCell>
                                    <TableCell><Skeleton width={80} /></TableCell>
                                    <TableCell><Skeleton width={80} /></TableCell>
                                    <TableCell><Skeleton width={80} /></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...Array(5)].map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell><Skeleton /></TableCell>
                                        <TableCell><Skeleton width={60} /></TableCell>
                                        <TableCell><Skeleton width={60} /></TableCell>
                                        <TableCell><Skeleton width={80} /></TableCell>
                                        <TableCell><Skeleton width={80} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <>
                        {/* Balance summary — read-only reconciliation */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                gap: 2,
                                mb: 2,
                                mt: 1,
                            }}
                        >
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(58, 222, 204, 0.08)', border: '1px solid rgba(58, 222, 204, 0.25)' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                                    Client App Balance
                                </Typography>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#3adecc' }}>
                                    {formatCurrency(balanceSummary.clientAppBalance)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                                    What the client sees in the app
                                </Typography>
                            </Box>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(33, 150, 243, 0.08)', border: '1px solid rgba(33, 150, 243, 0.25)' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                                    Statement Balance
                                </Typography>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#64b5f6' }}>
                                    {formatCurrency(balanceSummary.statementBalance)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                                    Latest registered ledger entry
                                </Typography>
                            </Box>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.25)' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                                    Pending (deducted from app)
                                </Typography>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#ffb74d' }}>
                                    {balanceSummary.pendingDeductedTotal > 0
                                        ? `-${formatCurrency(balanceSummary.pendingDeductedTotal).slice(1)}`
                                        : formatCurrency(0)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                                    Not yet in statement
                                </Typography>
                            </Box>
                        </Box>

                        {(balanceSummary.isExplainedByPending ||
                            (balanceSummary.isMismatch && !balanceSummary.isExplainedByPending)) && (
                            <Typography
                                sx={{
                                    mb: 1.5,
                                    fontSize: '0.72rem',
                                    lineHeight: 1.35,
                                    color: balanceSummary.isExplainedByPending
                                        ? 'rgba(100, 181, 246, 0.9)'
                                        : 'rgba(255, 183, 77, 0.95)',
                                }}
                            >
                                {balanceSummary.isExplainedByPending
                                    ? `Reconciled: ${formatCurrency(balanceSummary.statementBalance)} − ${formatCurrency(balanceSummary.pendingDeductedTotal)} pending = ${formatCurrency(balanceSummary.clientAppBalance)} (app)`
                                    : `Unexplained gap of ${formatCurrency(Math.abs(balanceSummary.unexplainedDifference))} — review before Fix Balance.`}
                            </Typography>
                        )}

                        {pendingSummary.hasPending && (
                            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)' }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                                    Pending Transactions (not in statement)
                                </Typography>
                                {pendingSummary.pendingWithdrawalsDeducted.map((txn) => (
                                    <Box key={txn.id || txn.doc_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.8rem' }}>
                                        <Typography sx={{ fontSize: '0.8rem' }}>
                                            Withdrawal — {formatStatus(txn.request_status)} (deducted from app)
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', color: '#ffb74d' }}>
                                            -{formatCurrency(txn.pendingAmount)}
                                        </Typography>
                                    </Box>
                                ))}
                                {pendingSummary.pendingWithdrawalsApproved.map((txn) => (
                                    <Box key={txn.id || txn.doc_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.8rem' }}>
                                        <Typography sx={{ fontSize: '0.8rem' }}>
                                            Withdrawal — Approved, awaiting payment
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', color: '#ffb74d' }}>
                                            -{formatCurrency(txn.pendingAmount)}
                                        </Typography>
                                    </Box>
                                ))}
                                {pendingSummary.pendingDeposits.map((txn) => (
                                    <Box key={txn.id || txn.doc_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.8rem' }}>
                                        <Typography sx={{ fontSize: '0.8rem' }}>
                                            Deposit — Pending (not in balance)
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.8rem', color: '#81c784' }}>
                                            +{formatCurrency(txn.pendingAmount)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {isMobile ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                    maxHeight: "calc(100vh - 320px)",
                                    overflowY: "auto",
                                    pb: 1,
                                }}
                            >
                                {ledgersWithBalance?.length > 0 ? (
                                    ledgersWithBalance.map((ledger) => {
                                        const debit =
                                            ledger?.transection_type === 2
                                                ? formatCurrency(ledger?.new_txn_amount || ledger?.amount)
                                                : "--";
                                        const credit =
                                            ledger?.transection_type === 0 || ledger?.transection_type === 4
                                                ? formatCurrency(ledger?.new_txn_amount || ledger?.amount)
                                                : "--";
                                        const balance = formatCurrency(
                                            ledger?.displayBalance !== null && ledger?.displayBalance !== undefined
                                                ? ledger.displayBalance
                                                : ledger?.current_balance || ledger?.balance || 0
                                        );

                                        return (
                                            <Box
                                                key={ledger?.id}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 1 }}>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                                            Request
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "0.8rem", color: "text.primary" }}>
                                                            {formatCompactDate(ledger?.request_date || ledger?.date)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "right" }}>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                                            Conclusion
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "0.8rem", color: "text.primary" }}>
                                                            {formatCompactDate(ledger?.conclusion_date || ledger?.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                                                    <Chip
                                                        label={getTransactionTypeName(ledger?.transection_type)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getTransactionTypeColor(ledger?.transection_type),
                                                            color: "#fff",
                                                            fontWeight: 700,
                                                            fontSize: "0.7rem",
                                                            height: 24,
                                                        }}
                                                    />
                                                    <Chip
                                                        label={formatStatus(ledger?.request_status)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getStatusColor(ledger?.request_status),
                                                            color: "#fff",
                                                            fontWeight: 700,
                                                            fontSize: "0.7rem",
                                                            height: 24,
                                                        }}
                                                    />
                                                </Box>

                                                {ledger?.transection_type === LEDGER_TYPE.YIELD && ledger?.yield_product_name && (
                                                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 1 }}>
                                                        {ledger.yield_product_name}
                                                    </Typography>
                                                )}

                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 1fr",
                                                        gap: 1,
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>Debit</Typography>
                                                        <Typography sx={{ color: "text.primary" }}>{debit}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>Credit</Typography>
                                                        <Typography sx={{ color: "text.primary" }}>{credit}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>Profit</Typography>
                                                        <Typography sx={{ color: "text.primary" }}>{formatProfitPercentDisplay(ledger)}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>Balance</Typography>
                                                        <Typography sx={{ color: "primary.light", fontWeight: 600 }}>{balance}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Typography variant="body1" align="center" sx={{ color: "text.secondary", py: 3 }}>
                                        No ledgers found for this customer.
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                        <TableContainer sx={{
                                height: 'calc(90vh - 200px)',
                                maxHeight: '700px',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                width: '100%'
                            }}>
                            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                                <TableHead sx={{
                                    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                                    fontFamily: "Outfit",
                                    borderRadius: '10px',
                                    textAlign: "center",
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1 
                                }}>
                                    <TableRow>
                                        <TableCell sx={{ color: "#fff", width: '11%', fontSize: '0.9375rem', padding: '10px' }}>Request Date</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '11%', fontSize: '0.9375rem', padding: '10px' }}>Conclusion Date</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '12%', fontSize: '0.9375rem', padding: '10px' }}>Transaction ID</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '11%', fontSize: '0.9375rem', padding: '10px' }}>Type</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '9%', fontSize: '0.9375rem', padding: '10px' }}>Debit</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '9%', fontSize: '0.9375rem', padding: '10px' }}>Credit</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '9%', fontSize: '0.9375rem', padding: '10px' }}>Status</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '8%', fontSize: '0.9375rem', padding: '10px' }}>Profit (%)</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '10%', fontSize: '0.9375rem', padding: '10px' }}>Performance</TableCell>
                                        <TableCell sx={{ color: "#fff", width: '11%', fontSize: '0.9375rem', padding: '10px' }}>Balance</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ledgersWithBalance?.length > 0 ? (
                                        ledgersWithBalance?.map((ledger) => (
                                            <TableRow key={ledger?.id}>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    <Tooltip title={formatCompactDate(ledger?.request_date || ledger?.date)}>
                                                        <Typography sx={{ 
                                                            fontSize: '0.875rem',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {formatCompactDate(ledger?.request_date || ledger?.date)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    <Tooltip title={formatCompactDate(ledger?.conclusion_date || ledger?.date)}>
                                                        <Typography sx={{ 
                                                            fontSize: '0.875rem',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {formatCompactDate(ledger?.conclusion_date || ledger?.date)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    <Tooltip 
                                                        title={ledger?.doc_id || ledger?.id || 'N/A'}
                                                        arrow
                                                        placement="top"
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    fontSize: '0.875rem',
                                                                    fontFamily: 'monospace',
                                                                    maxWidth: '400px',
                                                                    wordBreak: 'break-all',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                                                    padding: '8px 12px'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Typography 
                                                            sx={{ 
                                                                fontFamily: 'monospace', 
                                                                fontSize: '0.6875rem',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                color: 'rgba(255, 255, 255, 0.7)'
                                                            }}
                                                        >
                                                            {ledger?.doc_id || ledger?.id || 'N/A'}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    <Box>
                                                        <Chip 
                                                            label={getTransactionTypeName(ledger?.transection_type)} 
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getTransactionTypeColor(ledger?.transection_type),
                                                                color: '#fff',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.75rem',
                                                                height: '24px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 6px'
                                                                }
                                                            }}
                                                        />
                                                        {ledger?.transection_type === LEDGER_TYPE.YIELD && ledger?.yield_product_name && (
                                                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', mt: 0.25, lineHeight: 1.2 }}>
                                                                {ledger.yield_product_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    {ledger?.transection_type === 2 ? formatCurrency(ledger?.new_txn_amount || ledger?.amount) : "--"}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    {(ledger?.transection_type === 0 || ledger?.transection_type === 4) ? formatCurrency(ledger?.new_txn_amount || ledger?.amount) : "--"}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    <Chip 
                                                        label={formatStatus(ledger?.request_status)} 
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getStatusColor(ledger?.request_status),
                                                            color: '#fff',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.8125rem',
                                                            height: '26px',
                                                            '& .MuiChip-label': {
                                                                padding: '0 8px'
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    {formatProfitPercentDisplay(ledger)}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontSize: '0.875rem',
                                                    padding: '10px',
                                                    color: ledger?.profit_loss_balance < 0 ? 'red' :
                                                        ledger?.profit_loss_balance > 0 ? 'green' : 'inherit'
                                                }}>
                                                    {formatCurrency(ledger?.profit_loss_balance || 0)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem', padding: '10px' }}>
                                                    {formatCurrency(ledger?.displayBalance !== null && ledger?.displayBalance !== undefined ? ledger.displayBalance : (ledger?.current_balance || ledger?.balance || 0))}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={10}>
                                                <Typography variant="body1" align="center">No ledgers found for this customer.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        )}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            marginTop: '16px',
                            gap: 0.5,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                                    Client App Balance:
                                </Typography>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#3adecc' }}>
                                    {formatCurrency(latestNetBalance)}
                                </Typography>
                            </Box>
                            {balanceSummary.pendingDeductedTotal > 0 && (
                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                                    Statement {formatCurrency(balanceSummary.statementBalance)} − Pending{' '}
                                    {formatCurrency(balanceSummary.pendingDeductedTotal)}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ViewLedgersModel;
